import { formatNumber } from 'i18n/format';
import { useTranslation } from 'react-i18next';
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTokens } from 'theme';
import { isAssistantNotFound } from 'api/assistant';
import {
  useDeleteDocument,
  useKnowledge,
  useKnowledgeDocument,
  useKnowledgeSearch,
  usePublishEntries,
  usePublishDocument,
  useReindexDocument,
  useRemoveEntry,
  useSaveEntry,
  useUploadDocument,
} from 'api/knowledge';
import {
  ACCEPTED_FILES,
  KIND_HINTS,
  KIND_LABELS,
  KIND_ORDER,
  MAX_FILE_MB,
  STATUS_COLORS,
  STATUS_LABELS,
  embeddingsText,
  formatWhen,
  hitTitle,
  knowledgeErrorText,
  slugify,
} from './knowledgeCopy';

const MONO = "ui-monospace, 'JetBrains Mono', 'SFMono-Regular', Menlo, Consolas, monospace";
const PREVIEW_CHARS = 160;
const HIT_CHARS = 700;
const KnowledgeWorkspace = lazy(() => import('./KnowledgeWorkspace'));

const preview = (text, max = PREVIEW_CHARS) => {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

const kindRank = (kind) => {
  const index = KIND_ORDER.indexOf(kind);
  return index === -1 ? KIND_ORDER.length : index;
};

const byKindAndTitle = (a, b) =>
  kindRank(a.kind) - kindRank(b.kind) || String(a.title).localeCompare(String(b.title), 'ru');

function Header({ embeddings }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Tooltip title={translate('assistant.knowledgePageBackToAssistant')}>
          <IconButton component={RouterLink} to="/console/assistant" aria-label={translate('assistant.knowledgePageBackToAssistant')} size="small">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <MenuBookOutlinedIcon sx={{ color: t.link }} />
        <Typography variant="h5" fontWeight={800}>{translate('assistant.assistantPageKnowledgeBase')}</Typography>
        <Chip size="small" label={translate('assistant.knowledgePageLab')} sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
      </Box>
      <Typography variant="body2" sx={{ color: t.inkMuted, mt: 0.5 }}>{translate('assistant.knowledgePageProjectRulesTermsMetricsCaveatsPreparedQueries')}{embeddingsText(embeddings)}
      </Typography>
    </Box>
  );
}

function ConfirmDialog({ open, title, text, busy, error, onConfirm, onClose }) {
  const { t: translate } = useTranslation();
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{text}</Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{translate('assistant.conversationListCancel')}</Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={busy}>
          {busy ? <CircularProgress size={18} color="inherit" /> : translate('assistant.knowledgePageDelete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One entry's form: a new one, or the published entry edited into a draft. */
export function EntryDialog({ open, entry, onClose, isNew = false }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const save = useSaveEntry();
  const editing = Boolean(entry) && !isNew;
  const [kind, setKind] = useState('rule');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [body, setBody] = useState('');
  const [queryText, setQueryText] = useState('');
  const [error, setError] = useState('');

  const { reset } = save;
  useEffect(() => {
    if (!open) return;
    setKind(entry?.kind || 'rule');
    setTitle(entry?.title || '');
    setSlug(entry?.slug || '');
    setSlugTouched(Boolean(entry));
    setBody(entry?.body || '');
    setQueryText(entry?.query ? JSON.stringify(entry.query, null, 2) : '');
    setError('');
    reset();
  }, [open, entry, reset]);

  const changeTitle = (value) => {
    setTitle(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const submit = (publish) => {
    let query = null;
    if (kind === 'query') {
      try {
        query = JSON.parse(queryText || '');
      } catch (e) {
        query = null;
      }
      if (!query || typeof query !== 'object' || Array.isArray(query)) {
        setError(translate('assistant.knowledgePageTheQueryMustBeACubeQuery'));
        return;
      }
    }
    setError('');
    save.mutate(
      { slug, kind, title, body, query, publish },
      { onSuccess: onClose, onError: (err) => setError(knowledgeErrorText(err)) },
    );
  };

  const busy = save.isPending;
  const ready = title.trim() !== '' && slug !== '';
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{editing ? translate('assistant.knowledgePageEditEntry') : translate('assistant.knowledgePageNewEntry')}</DialogTitle>
      <DialogContent>
        <TextField
          select
          label={translate('assistant.diagnosticResultsKind')}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          disabled={busy || editing}
          helperText={KIND_HINTS()[kind]}
        >
          {KIND_ORDER.map((k) => (
            <MenuItem key={k} value={k}>{KIND_LABELS()[k]}</MenuItem>
          ))}
        </TextField>
        <TextField
          label={kind === 'query' ? translate('assistant.knowledgePageQuestion') : translate('assistant.conversationListTitle')}
          value={title}
          onChange={(e) => changeTitle(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          disabled={busy}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          label="Slug"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          fullWidth
          margin="normal"
          size="small"
          disabled={busy || editing}
          helperText={translate('assistant.knowledgePageLatinLettersWithHyphensTheSameSlug')}
          inputProps={{ maxLength: 80, style: { fontFamily: MONO } }}
        />
        <TextField
          label={kind === 'query' ? translate('assistant.knowledgePageExplanation') : translate('assistant.diagnosticResultsText')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          multiline
          minRows={3}
          disabled={busy}
          inputProps={{ maxLength: 4000 }}
        />
        {kind === 'query' && (
          <TextField
            label={translate('assistant.knowledgePageCubeQueryJSON')}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            fullWidth
            margin="normal"
            size="small"
            multiline
            minRows={4}
            disabled={busy}
            inputProps={{ style: { fontFamily: MONO, fontSize: '0.8rem' } }}
          />
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted, mt: 1 }}>{translate('assistant.knowledgePageOnlyYouCanSeeTheDraftThe')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{translate('assistant.conversationListCancel')}</Button>
        <Button onClick={() => submit(false)} disabled={busy || !ready}>{translate('assistant.knowledgePageSaveDraft')}</Button>
        <Button variant="contained" onClick={() => submit(true)} disabled={busy || !ready}>
          {busy ? <CircularProgress size={18} color="inherit" /> : translate('assistant.knowledgePagePublish')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EntryText({ entry }) {
  useTranslation();
  const t = useTokens();
  return (
    <>
      {entry.body && <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{preview(entry.body)}</Typography>}
      {entry.query && (
        <Typography variant="caption" sx={{ display: 'block', fontFamily: MONO, color: t.inkMuted, overflowWrap: 'anywhere' }}>
          {preview(JSON.stringify(entry.query), 120)}
        </Typography>
      )}
    </>
  );
}

function EntriesTab({ data, canWrite }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const publish = usePublishEntries();
  const remove = useRemoveEntry();
  const [dialog, setDialog] = useState(null); // {entry: object|null}
  const [removing, setRemoving] = useState(null); // {slug, title, published}
  const [error, setError] = useState('');

  const published = [...(data.published || [])].sort(byKindAndTitle);
  const drafts = [...(data.drafts || [])].sort(byKindAndTitle);
  const busy = publish.isPending || remove.isPending;

  const act = (mutation, variables) => {
    setError('');
    mutation.mutate(variables, { onError: (err) => setError(knowledgeErrorText(err)) });
  };
  const confirmRemove = () =>
    remove.mutate(
      { slug: removing.slug, published: removing.published },
      { onSuccess: () => setRemoving(null), onError: (err) => setError(knowledgeErrorText(err)) },
    );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: t.inkMuted, flexGrow: 1 }}>
          {canWrite
            ? translate('assistant.knowledgePageAnEntryAppliesToEveryoneInThe')
            : translate('assistant.knowledgePageProjectOwnersAndAdministratorsCanEditEntries')}
        </Typography>
        {canWrite && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog({ entry: null })}>{translate('assistant.knowledgePageAddEntry')}</Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {drafts.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: t.raised }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{translate('assistant.knowledgePageYourDraftsNotYetActive')}</Typography>
            <Button size="small" disabled={busy} onClick={() => act(publish, drafts.map((d) => d.slug))}>{translate('assistant.knowledgePagePublishAll')}</Button>
          </Box>
          {drafts.map((d) => (
            <Box key={d.slug} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}>
              <Chip size="small" label={d.removal ? translate('assistant.knowledgePageDelete2') : KIND_LABELS()[d.kind] || d.kind} color={d.removal ? 'error' : 'default'} />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>{d.title}</Typography>
                <EntryText entry={d} />
              </Box>
              <Button size="small" disabled={busy} onClick={() => act(publish, [d.slug])}>{translate('assistant.knowledgePagePublish')}</Button>
              <Button size="small" color="inherit" disabled={busy} onClick={() => act(remove, { slug: d.slug, published: false })}>{translate('assistant.knowledgePageRemove')}</Button>
            </Box>
          ))}
        </Paper>
      )}

      {published.length === 0 ? (
        <Typography variant="body2" sx={{ color: t.inkMuted, py: 3, textAlign: 'center' }}>{translate('assistant.knowledgePageNoPublishedEntriesYet')}</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('assistant.diagnosticResultsKind')}</TableCell>
                <TableCell>{translate('assistant.conversationListTitle')}</TableCell>
                <TableCell>{translate('assistant.diagnosticResultsText')}</TableCell>
                {canWrite && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {published.map((entry) => (
                <TableRow key={entry.slug} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip size="small" label={KIND_LABELS()[entry.kind] || entry.kind} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 140, overflowWrap: 'anywhere' }}>
                    <Typography variant="body2" fontWeight={600}>{entry.title}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: MONO, color: t.inkMuted }}>{entry.slug}</Typography>
                  </TableCell>
                  <TableCell><EntryText entry={entry} /></TableCell>
                  {canWrite && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={translate('assistant.knowledgePageEdit')}>
                        <IconButton size="small" aria-label={translate('assistant.knowledgePageEdit2', { value1: entry.slug })} onClick={() => setDialog({ entry })}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('assistant.knowledgePageRemoveFromKnowledge')}>
                        <IconButton
                          size="small"
                          aria-label={translate('assistant.knowledgePageDelete3', { value1: entry.slug })}
                          onClick={() => setRemoving({ slug: entry.slug, title: entry.title, published: true })}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EntryDialog open={Boolean(dialog)} entry={dialog?.entry || null} onClose={() => setDialog(null)} />
      <ConfirmDialog
        open={Boolean(removing)}
        title={translate('assistant.knowledgePageDeleteEntry')}
        text={translate('assistant.knowledgePageWillNoLongerApplyToAnyoneIn', { value1: removing?.title || '' })}
        busy={remove.isPending}
        error={error}
        onConfirm={confirmRemove}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}

function DocumentDraftDialog({ document, onClose }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [offsets, setOffsets] = useState([0]);
  const [error, setError] = useState('');
  const offset = offsets[offsets.length - 1];
  const page = useKnowledgeDocument(document.id, { offset });
  const publish = usePublishDocument();
  const detail = page.data;
  const ready = !page.isFetching && !page.isError && detail?.id === document.id && detail?.status === 'draft';
  const next = detail?.next_offset;
  const hasNext = Number.isInteger(next) && next > offset;
  const submit = () => {
    if (!ready || publish.isPending) return;
    setError('');
    publish.mutate(document.id, {
      onSuccess: onClose,
      onError: (err) => setError(knowledgeErrorText(err)),
    });
  };
  return (
    <Dialog open onClose={publish.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{translate('assistant.knowledgePageDocumentDraft')}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>{detail?.title || document.title}</Typography>
        <Typography variant="body2" sx={{ color: t.inkMuted, mb: 2 }}>{translate('assistant.knowledgePageReviewTheTextBeforePublicationTheDraft')}</Typography>
        {page.isLoading && <CircularProgress size={24} aria-label={translate('assistant.knowledgePageLoadingDocumentText')} />}
        {page.isError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => page.refetch()}>{translate('assistant.answerWidgetRetry')}</Button>}>
            {knowledgeErrorText(page.error)}
          </Alert>
        )}
        {detail && !page.isError && (
          <>
            <TextField
              label={translate('assistant.knowledgePageDocumentText')}
              value={detail.text || ''}
              fullWidth
              multiline
              minRows={8}
              maxRows={16}
              InputProps={{ readOnly: true }}
              inputProps={{ style: { fontFamily: MONO, fontSize: '0.8125rem' } }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Typography variant="caption" sx={{ color: t.inkMuted, flexGrow: 1 }}>
                {translate('assistant.documentCharacters', { from: formatNumber(detail.offset + 1), to: formatNumber(detail.next_offset ?? detail.chars), total: formatNumber(detail.chars) })}</Typography>
              <Button size="small" disabled={offsets.length === 1 || page.isFetching || publish.isPending} onClick={() => setOffsets((current) => current.slice(0, -1))}>{translate('assistant.knowledgePageBack')}</Button>
              <Button size="small" disabled={!hasNext || page.isFetching || publish.isPending} onClick={() => setOffsets((current) => [...current, next])}>{translate('assistant.knowledgePageReadMore')}</Button>
            </Box>
            {detail.status !== 'draft' && <Alert severity="info" sx={{ mt: 1 }}>{translate('assistant.knowledgePageTheDocumentIsAlreadyPublished')}</Alert>}
          </>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={publish.isPending}>{translate('assistant.conversationListCancel')}</Button>
        <Button variant="contained" disabled={!ready || publish.isPending} onClick={submit}>
          {publish.isPending ? <CircularProgress size={18} color="inherit" /> : translate('assistant.knowledgePagePublishDocument')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DocumentsTab({ data, canWrite }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const upload = useUploadDocument();
  const del = useDeleteDocument();
  const reindex = useReindexDocument();
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState(null); // document item
  const [reviewing, setReviewing] = useState(null);
  const documents = data.documents || [];
  const busy = upload.isPending || del.isPending || reindex.isPending;

  const onFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(translate('assistant.knowledgeCopyTheFileExceedsMB', { value1: MAX_FILE_MB }));
      return;
    }
    setError('');
    upload.mutate({ file, title: title.trim() }, {
      onSuccess: () => setTitle(''),
      onError: (err) => setError(knowledgeErrorText(err)),
    });
  };
  const act = (mutation, id) => {
    setError('');
    mutation.mutate(id, { onError: (err) => setError(knowledgeErrorText(err)) });
  };
  const confirmRemove = () =>
    del.mutate(removing.id, {
      onSuccess: () => setRemoving(null),
      onError: (err) => setError(knowledgeErrorText(err)),
    });

  return (
    <>
      <Typography variant="body2" sx={{ color: t.inkMuted, mb: 2 }}>{translate('assistant.knowledgePageUploadAPolicyMethodologyOrDescriptionThe')}</Typography>
      {canWrite && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label={translate('assistant.knowledgePageTitleOptional')}
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            sx={{ minWidth: 280 }}
            inputProps={{ maxLength: 200 }}
          />
          <Button component="label" variant="contained" size="small" startIcon={<UploadFileIcon />} disabled={busy}>
            {upload.isPending ? translate('assistant.knowledgePageUploading') : translate('assistant.knowledgePageUploadFile')}
            <input hidden type="file" accept={ACCEPTED_FILES} onChange={onFile} data-testid="knowledge-file" />
          </Button>
          <Typography variant="caption" sx={{ color: t.inkMuted }}>{translate('assistant.uploadFileTypes', { size: formatNumber(MAX_FILE_MB) })}</Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {documents.length === 0 ? (
        <Typography variant="body2" sx={{ color: t.inkMuted, py: 3, textAlign: 'center' }}>{translate('assistant.knowledgePageNoDocumentsYet')}</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('assistant.conversationListTitle')}</TableCell>
                <TableCell>{translate('assistant.knowledgePageFile')}</TableCell>
                <TableCell>{translate('assistant.knowledgePageStatus')}</TableCell>
                <TableCell align="right">{translate('assistant.knowledgePageFragments')}</TableCell>
                <TableCell>{translate('assistant.knowledgePageUploaded')}</TableCell>
                {canWrite && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{d.title}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: MONO, color: t.inkMuted }}>{d.filename || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_LABELS()[d.status] || d.status} color={STATUS_COLORS[d.status] || 'default'} variant="outlined" />
                    {d.status === 'draft' && (
                      <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted }}>{translate('assistant.knowledgePageNotUsedInAnswersUntilPublished')}</Typography>
                    )}
                    {d.error && (
                      <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted }}>
                        {d.error === 'embeddings' ? translate('assistant.knowledgePageOnlyKeywordSearchIsAvailableReindexTo') : translate('assistant.knowledgePageCouldNotIndexTheDocumentPleaseTry')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{d.chunk_count}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: t.inkMuted }}>
                      {formatWhen(d.created_at)}{d.uploaded_by ? ` · ${d.uploaded_by}` : ''}
                    </Typography>
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {d.status === 'draft' ? (
                        <Tooltip title={translate('assistant.knowledgePageViewAndPublish')}>
                          <IconButton size="small" aria-label={translate('assistant.knowledgePageViewDraft', { value1: d.title })} disabled={busy} onClick={() => setReviewing(d)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : <Tooltip title={translate('assistant.knowledgePageReindex')}>
                        <span>
                          <IconButton size="small" aria-label={translate('assistant.knowledgePageReindex2', { value1: d.title })} disabled={busy || d.status === 'indexing'} onClick={() => act(reindex, d.id)}>
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>}
                      <Tooltip title={translate('assistant.knowledgePageDeleteDocument')}>
                        <IconButton size="small" aria-label={translate('assistant.knowledgePageDelete3', { value1: d.title })} disabled={busy} onClick={() => setRemoving(d)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {canWrite && reviewing && <DocumentDraftDialog key={reviewing.id} document={reviewing} onClose={() => setReviewing(null)} />}
      <ConfirmDialog
        open={Boolean(removing)}
        title={translate('assistant.knowledgePageDeleteDocument2')}
        text={translate('assistant.knowledgePageAndItsFragmentsWillBeRemovedFrom', { value1: removing?.title || '' })}
        busy={del.isPending}
        error={error}
        onConfirm={confirmRemove}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}

function Hit({ hit }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const label = hit.entry ? KIND_LABELS()[hit.entry.kind] || hit.entry.kind : translate('assistant.knowledgePageDocument');
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Chip size="small" label={label} color={hit.entry ? 'primary' : 'default'} variant="outlined" />
        <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>{hitTitle(hit)}</Typography>
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{preview(hit.text, HIT_CHARS)}</Typography>
      {hit.entry?.query && (
        <Typography variant="caption" sx={{ display: 'block', fontFamily: MONO, color: t.inkMuted, mt: 0.5, overflowWrap: 'anywhere' }}>
          {JSON.stringify(hit.entry.query)}
        </Typography>
      )}
    </Paper>
  );
}

function SearchTab() {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const search = useKnowledgeSearch();
  const [q, setQ] = useState('');
  const run = () => {
    if (q.trim()) search.mutate(q.trim());
  };
  const hits = search.data?.hits || [];
  return (
    <>
      <Typography variant="body2" sx={{ color: t.inkMuted, mb: 2 }}>{translate('assistant.knowledgePageEnterAQuestionToSeeTheKnowledge')}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={translate('assistant.knowledgePageForExampleHowDoWeDefineAn')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') run();
          }}
          inputProps={{ 'aria-label': translate('assistant.knowledgePageSearchQuery'), maxLength: 1000 }}
        />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={run} disabled={search.isPending || !q.trim()}>{translate('assistant.knowledgePageSearch')}</Button>
      </Box>
      {search.isError && <Alert severity="error" sx={{ mb: 2 }}>{knowledgeErrorText(search.error)}</Alert>}
      {search.isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {search.isSuccess && hits.length === 0 && (
        <Typography variant="body2" sx={{ color: t.inkMuted, py: 3, textAlign: 'center' }}>{translate('assistant.knowledgePageNoCloseMatchesFound')}</Typography>
      )}
      {hits.map((hit, i) => (
        <Hit key={`${hit.kind}-${i}`} hit={hit} />
      ))}
    </>
  );
}

export default function KnowledgePage() {
  const { t: translate } = useTranslation();
  const knowledge = useKnowledge();
  const [tab, setTab] = useState('files');
  const navigationGuard = useRef(null);
  const registerGuard = useCallback((guard) => { navigationGuard.current = guard; }, []);
  const changeTab = (_event, value) => {
    const proceed = () => setTab(value);
    if (navigationGuard.current) navigationGuard.current(proceed);
    else proceed();
  };

  if (knowledge.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (knowledge.isError) {
    return isAssistantNotFound(knowledge.error) ? (
      <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>{translate('assistant.assistantPagePageNotFound')}</Typography>
    ) : (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => knowledge.refetch()}>{translate('assistant.answerWidgetRetry')}</Button>}>{translate('assistant.knowledgePageCouldNotLoadTheKnowledgeBase')}</Alert>
    );
  }

  const data = knowledge.data || {};
  const canWrite = Boolean(data.can_write);
  const documents = data.documents || [];
  return (
    <Box>
      <Header embeddings={data.embeddings} />
      <Paper variant="outlined">
        <Tabs value={tab} onChange={changeTab} variant="scrollable" sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="files" label={translate('assistant.knowledgePageFiles')} />
          <Tab value="entries" label={translate('assistant.knowledgePageEntries', { value1: (data.published || []).length })} />
          <Tab value="documents" label={translate('assistant.knowledgePageDocuments', { value1: documents.length })} />
          <Tab value="search" label={translate('assistant.knowledgePageTestSearch')} />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 'files' && <Suspense fallback={<CircularProgress size={24} />}><KnowledgeWorkspace canWrite={canWrite} onGuardReady={registerGuard} /></Suspense>}
          {tab === 'entries' && <EntriesTab data={data} canWrite={canWrite} />}
          {tab === 'documents' && <DocumentsTab data={data} canWrite={canWrite} />}
          {tab === 'search' && <SearchTab />}
        </Box>
      </Paper>
    </Box>
  );
}
